import { GridFilterItem, GridFilterModel } from '@mui/x-data-grid-premium';
import dayjs from 'dayjs';

import { FilterFieldsType } from '../../../redux/clientConfiguration/clientConfigurationSlice';
import {
 deriveGridOperator,
 isModelValue,
 normalizeToIds,
} from './filterModel';

const FIELD_PREFIX = 'f_';
const OPERATOR_PREFIX = 'fo_';

const DATETIME_PATTERN =
 /^(\d{4}\.\d{2}\.\d{2} \d{2}:\d{2}:\d{2}|x)-(\d{4}\.\d{2}\.\d{2} \d{2}:\d{2}:\d{2}|x)$/;

const isDatetimeValue = (value: unknown): value is string =>
 typeof value === 'string' && DATETIME_PATTERN.test(value);

export const hasActiveFilterFields = (
 filterFields: FilterFieldsType | null | undefined,
): boolean =>
 !!filterFields &&
 Object.values(filterFields.fields).some((v) => !isEmptyValue(v));

const normalizeFilterFields = (
 filterFields: FilterFieldsType | null | undefined,
): { fields: Record<string, unknown>; operators: Record<string, string> } | null => {
 if (!filterFields || !hasActiveFilterFields(filterFields)) return null;

 const fields: Record<string, unknown> = {};
 const operators: Record<string, string> = {};

 Object.entries(filterFields.fields)
  .filter(([, value]) => !isEmptyValue(value))
  .sort(([fieldA], [fieldB]) => fieldA.localeCompare(fieldB))
  .forEach(([field, value]) => {
   fields[field] = value;
   const operator = filterFields.operators[field];
   operators[field] =
    operator !== null && operator !== undefined && operator !== ''
     ? operator
     : 'equals';
  });

 return { fields, operators };
};

export const areFilterFieldsEqual = (
 a: FilterFieldsType | null | undefined,
 b: FilterFieldsType | null | undefined,
): boolean => {
 const normalizedA = normalizeFilterFields(a);
 const normalizedB = normalizeFilterFields(b);

 if (normalizedA === null && normalizedB === null) return true;
 if (normalizedA === null || normalizedB === null) return false;

 return (
  JSON.stringify(normalizedA.fields) === JSON.stringify(normalizedB.fields) &&
  JSON.stringify(normalizedA.operators) === JSON.stringify(normalizedB.operators)
 );
};

const isEmptyValue = (
 value: FilterFieldsType['fields'][string],
): boolean => {
 if (value === null || value === undefined || value === '') return true;
 if (Array.isArray(value) && value.length === 0) return true;
 return false;
};

const stringifyFieldValue = (
 value: NonNullable<FilterFieldsType['fields'][string]>,
): string => {
 if (Array.isArray(value) || (typeof value === 'object' && value !== null)) {
  return JSON.stringify(value);
 }
 return String(value);
};

const parseFieldValue = (
 raw: string,
): FilterFieldsType['fields'][string] => {
 if (raw.startsWith('[') || raw.startsWith('{')) {
  try {
   return JSON.parse(raw) as Array<{ [key: string]: string | number }>;
  } catch {
   // fall through to string/number
  }
 }
 const trimmed = raw.trim();
 if (trimmed !== '') {
  const num = Number(trimmed);
  if (!Number.isNaN(num)) return num;
 }
 return raw;
};

export const serializeFilters = (
 filterFields: FilterFieldsType | null | undefined,
): string | null => {
 if (!filterFields) return null;

 const params = new URLSearchParams();
 let hasAny = false;

 Object.entries(filterFields.fields).forEach(([field, value]) => {
  if (isEmptyValue(value)) return;
  params.set(`${FIELD_PREFIX}${field}`, stringifyFieldValue(value!));
  hasAny = true;
 });

 if (!hasAny) return null;

 Object.entries(filterFields.operators).forEach(([field, op]) => {
  if (op !== null && op !== '') {
   params.set(`${OPERATOR_PREFIX}${field}`, op);
  }
 });

 return params.toString();
};

export const deserializeFilters = (
 searchString: string,
): FilterFieldsType | null => {
 if (!searchString) return null;
 try {
  const params = new URLSearchParams(searchString);
  const fields: FilterFieldsType['fields'] = {};
  const operators: FilterFieldsType['operators'] = {};
  let hasAny = false;

  params.forEach((value, key) => {
   if (key.startsWith(FIELD_PREFIX)) {
    const field = key.slice(FIELD_PREFIX.length);
    fields[field] = parseFieldValue(value);
    hasAny = true;
   } else if (key.startsWith(OPERATOR_PREFIX)) {
    const field = key.slice(OPERATOR_PREFIX.length);
    operators[field] = value;
   }
  });

  if (!hasAny) return null;
  return { fields, operators };
 } catch {
  return null;
 }
};

export const filterFieldsToGridItems = (
 filterFields: FilterFieldsType,
): GridFilterItem[] => {
 const { fields, operators } = filterFields;
 const items: GridFilterItem[] = [];

 Object.entries(fields).forEach(([field, value]) => {
  if (isEmptyValue(value)) return;

  if (isDatetimeValue(value)) {
   const separatorIndex = value.indexOf('-');
   const fromRaw = value.slice(0, separatorIndex);
   const toRaw = value.slice(separatorIndex + 1);

   if (fromRaw && fromRaw !== 'x') {
    const fromDate = dayjs(fromRaw);
    if (fromDate.isValid()) {
     items.push({
      id: `${field}_from`,
      field,
      operator: 'onOrAfter',
      value: fromDate.toDate(),
     });
    }
   }

   if (toRaw && toRaw !== 'x') {
    const toDate = dayjs(toRaw);
    if (toDate.isValid()) {
     items.push({
      id: `${field}_to`,
      field,
      operator: 'onOrBefore',
      value: toDate.toDate(),
     });
    }
   }

   return;
  }

  const isModel = isModelValue(value);
  const normalizedValue = normalizeToIds(value);
  items.push({
   id: `${field}_${JSON.stringify(normalizedValue).replace(/[^a-zA-Z0-9]/g, '')}`,
   field,
   operator: deriveGridOperator(isModel, operators[field]),
   value: normalizedValue,
  });
 });

 return items;
};

export const updateUrlFilterParams = (
 filterFields: FilterFieldsType | null,
): void => {
 const urlParams = new URLSearchParams(window.location.search);

 const keysToDelete: string[] = [];
 urlParams.forEach((_, key) => {
  if (key.startsWith(FIELD_PREFIX) || key.startsWith(OPERATOR_PREFIX)) {
   keysToDelete.push(key);
  }
 });
 keysToDelete.forEach((k) => urlParams.delete(k));

 if (filterFields) {
  Object.entries(filterFields.fields).forEach(([field, value]) => {
   if (!isEmptyValue(value)) {
    urlParams.set(`${FIELD_PREFIX}${field}`, stringifyFieldValue(value!));
   }
  });
  Object.entries(filterFields.operators).forEach(([field, op]) => {
   if (op !== null && op !== '') {
    urlParams.set(`${OPERATOR_PREFIX}${field}`, op);
   }
  });
 }

 const newSearch = urlParams.toString();
 const newUrl = newSearch
  ? `${window.location.pathname}?${newSearch}`
  : window.location.pathname;

 window.history.replaceState({}, '', newUrl);
};

export const syncFiltersFromPreset = (
 filterFields: FilterFieldsType | null | undefined,
 setGridFilterModel: (model: GridFilterModel) => void,
 options?: { updateUrl?: boolean },
): void => {
 const items = filterFields ? filterFieldsToGridItems(filterFields) : [];
 const model: GridFilterModel = { items };
 setGridFilterModel(model);
 if (options?.updateUrl !== false) {
  updateUrlFilterParams(filterFields ?? null);
 }
};

export const buildModelFilterFields = (
 field: string,
 item: { Id?: string | number; Name?: string | number },
): FilterFieldsType => ({
 fields: {
  [field]: [
   {
    Id: item.Id ?? '',
    Name: String(item.Name ?? item.Id ?? ''),
   },
  ],
 },
 operators: { [field]: 'equals' },
});

export const buildNavigationUrl = (
 path: string,
 filterFields: FilterFieldsType,
): string => {
 const qs = serializeFilters(filterFields);
 if (!qs) return path;
 return `${path}?${qs}`;
};
